// Firestore data access. Every read/write here is scoped to the signed-in uid,
// matching the per-tenant security rules. Exam profiles are the one shared,
// read-for-all collection; writing them happens server-side via the
// upsertExamProfile callable (see lib/functions.js), not here.

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// ---- user profile ----------------------------------------------------------

export async function ensureUserDoc(uid, email, name) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      ownerId: uid,
      email: email ?? null,
      displayName: name ?? null,
      selectedExamId: null,
      createdAt: serverTimestamp(),
    });
  }
}

export async function getUserDoc(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

export async function setSelectedExam(uid, examId) {
  await updateDoc(doc(db, 'users', uid), { ownerId: uid, selectedExamId: examId });
}

// ---- exam registry (reads only; writes are server-side) --------------------

export async function listExamProfiles() {
  const snap = await getDocs(collection(db, 'examProfiles'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getExamProfile(examId) {
  const snap = await getDoc(doc(db, 'examProfiles', examId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ---- library ---------------------------------------------------------------

export async function listLibrary(uid, examId) {
  const base = collection(db, 'library', uid, 'items');
  const q = examId
    ? query(base, where('examId', '==', examId), orderBy('createdAt', 'desc'), limit(200))
    : query(base, orderBy('createdAt', 'desc'), limit(200));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ itemId: d.id, ...d.data() }));
}

export async function toggleSaved(uid, itemId, saved) {
  await updateDoc(doc(db, 'library', uid, 'items', itemId), { ownerId: uid, saved });
}

export async function recordResponse(uid, itemId, selected) {
  await updateDoc(doc(db, 'library', uid, 'items', itemId), {
    ownerId: uid,
    response: {
      selectedOptionId: selected.id,
      correct: selected.correct,
      answeredAt: Date.now(),
    },
  });
}

// ---- progress --------------------------------------------------------------

export async function getProgress(uid) {
  const snap = await getDoc(doc(db, 'progress', uid));
  return snap.exists() ? snap.data() : null;
}

export async function bumpProgress(uid, examId, correct) {
  const ref = doc(db, 'progress', uid);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const byExam = data.byExam ?? {};
  const cur = byExam[examId] ?? { answered: 0, correct: 0 };
  byExam[examId] = {
    answered: cur.answered + 1,
    correct: cur.correct + (correct ? 1 : 0),
  };
  await setDoc(
    ref,
    {
      ownerId: uid,
      byExam,
      totalAnswered: (data.totalAnswered ?? 0) + 1,
      totalCorrect: (data.totalCorrect ?? 0) + (correct ? 1 : 0),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}
